import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getInitialEmail() {
  try {
    const raw = localStorage.getItem('user');
    const user = raw ? JSON.parse(raw) : null;
    return user?.email ? String(user.email).trim() : '';
  } catch {
    return '';
  }
}

function RequestDemandStep11() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const requestStep4Details = location.state?.requestStep4Details || {};
  const requestStep5Details = location.state?.requestStep5Details || {};
  const requestStep6Details = location.state?.requestStep6Details || {};
  const requestStep7Details = location.state?.requestStep7Details || {};
  const requestStep8Details = location.state?.requestStep8Details || {};
  const requestStep9Details = location.state?.requestStep9Details || {};
  const requestStep10Details = location.state?.requestStep10Details || {};
  const isStep5Skipped = location.state?.isStep5Skipped || false;
  const displayStep = isStep5Skipped ? 10 : 11;

  const [email, setEmail] = useState(getInitialEmail);
  const [dataVisibilityConsent, setDataVisibilityConsent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const canContinue = useMemo(
    () => isValidEmail(email.trim()) && Boolean(dataVisibilityConsent) && !submitting,
    [email, dataVisibilityConsent, submitting]
  );

  const handleContinue = async () => {
    if (!canContinue) return;

    setSubmitting(true);
    setSubmitError('');

    const requestStep11Details = {
      email: email.trim(),
      dataVisibilityConsent,
      allowProviderToSeeUserData: dataVisibilityConsent === 'share-with-provider',
    };

    let user = null;
    try {
      const raw = localStorage.getItem('user');
      user = raw ? JSON.parse(raw) : null;
    } catch {
      user = null;
    }

    const wizardPayload = {
      projectTitle,
      requestDetails,
      requestStep4Details,
      requestStep5Details,
      requestStep6Details,
      requestStep7Details,
      requestStep8Details,
      requestStep9Details,
      requestStep10Details,
      requestStep11Details,
    };

    const requestData = {
      userId: user?._id || user?.id,
      userName: user?.name || user?.fullName,
      userEmail: email.trim(),
      services: [{ serviceId, title: parentServiceTitle }],
      subService: selectedSubService,
      projectTitle,
      interventionCity: requestStep7Details.city,
      interventionRegion: requestStep7Details.region,
      about: projectTitle || undefined,
      customQuote: requestStep9Details.budget || requestStep9Details.customBudget,
      workDays: requestStep8Details.mode,
      workHours: requestStep8Details.mode === 'exact-date'
        ? [requestStep8Details.exactDate, requestStep8Details.exactTime].filter(Boolean).join(' ')
        : [requestStep8Details.beforeDate, requestStep8Details.beforeTime].filter(Boolean).join(' '),
      notes: [
        requestStep10Details.additionalDetails,
        JSON.stringify(wizardPayload),
      ].filter(Boolean).join('\n\n'),
      allowProviderToSeeUserData: requestStep11Details.allowProviderToSeeUserData,
      sendConfirmationEmail: true,
    };

    try {
      const response = await api.submitRequest(requestData);
      if (!response?.success) {
        throw new Error(response?.message || t('wizard.step11SaveError'));
      }

      navigate(`/service/${serviceId}/request-demand-step12`, {
        state: {
          selectedSubService,
          parentServiceTitle,
          projectTitle,
          isStep5Skipped,
          requestDetails,
          requestStep4Details,
          requestStep5Details,
          requestStep6Details,
          requestStep7Details,
          requestStep8Details,
          requestStep9Details,
          requestStep10Details,
          requestStep11Details,
          requestId: response.data?._id,
          emailSent: response.emailSent,
          emailError: response.emailError,
        },
      });
    } catch (error) {
      setSubmitError(error.message || t('wizard.step11SubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout title={`${t('wizard.step', { n: displayStep })} - ${t('wizard.step11Title')}`} rootClass="layout-1">
      <style>{`
        .request-step11-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step11-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step11-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step11-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.45rem;
        }
        .request-step11-description {
          color: #5f6b7a;
          margin-bottom: 2rem;
          max-width: 620px;
        }
        .request-step11-required {
          text-align: center;
          color: #ef4444;
          margin-bottom: 1rem;
          font-size: 1.2rem;
          line-height: 1;
        }
        .request-step11-label {
          color: #0f2b3d;
          font-size: 1.05rem;
          font-weight: 500;
          margin-bottom: 0.7rem;
        }
        .request-step11-input {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          height: 46px;
          padding: 0 0.9rem;
          color: #0f2b3d;
          background: #fff;
        }
        .request-step11-privacy-wrap {
          margin-top: 1.1rem;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          padding: 0.8rem;
        }
        .request-step11-privacy-title {
          color: #0f2b3d;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .request-step11-privacy-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.35rem;
          color: #0f2b3d;
        }
        .request-step11-privacy-note {
          color: #5f6b7a;
          font-size: 0.9rem;
          margin: 0.2rem 0 0;
        }
      
        .request-step11-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step11-page p-3 p-md-4">
            <div className="request-step11-content">
              <div className="request-step11-step">{t('wizard.step', { n: displayStep })}</div>
              <h2 className="request-step11-heading">{t('wizard.step11Heading')}</h2>
              <p className="request-step11-description">
                {t('wizard.step11Description')}
              </p>

              <div className="request-step11-required">*</div>

              <h3 className="request-step11-label">{t('wizard.step11EmailLabel')}</h3>
              <input
                className="request-step11-input"
                placeholder={t('wizard.step11EmailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />

              <div className="request-step11-privacy-wrap">
                <div className="request-step11-privacy-title">{t('wizard.step11PrivacyTitle')}</div>
                <label className="request-step11-privacy-option">
                  <input
                    type="radio"
                    name="data-visibility"
                    value="private"
                    checked={dataVisibilityConsent === 'private'}
                    onChange={(event) => setDataVisibilityConsent(event.target.value)}
                  />
                  <span>{t('wizard.step11PrivacyPrivate')}</span>
                </label>
                <label className="request-step11-privacy-option">
                  <input
                    type="radio"
                    name="data-visibility"
                    value="share-with-provider"
                    checked={dataVisibilityConsent === 'share-with-provider'}
                    onChange={(event) => setDataVisibilityConsent(event.target.value)}
                  />
                  <span>{t('wizard.step11PrivacyShare')}</span>
                </label>
                <p className="request-step11-privacy-note">
                  {t('wizard.step11PrivacyNote')}
                </p>
              </div>

              {submitError ? (
                <p className="text-danger mt-3 mb-0">{submitError}</p>
              ) : null}
            </div>

            <div className="request-step11-footer">
              <NioButton
                className="btn btn-primary"
                label={t('wizard.step11Previous')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${canContinue ? 'btn-primary' : 'btn-primary disabled'}`}
                label={submitting ? t('wizard.step11Submitting') : t('wizard.step11Confirm')}
                onClick={handleContinue}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep11;
